import { Button, PageTitle } from '@/components';
import { useGetCompany } from '@/services';
import {
	useRemoveEmployee,
	useUnlinkEmployeeFromUser,
} from '@/services/mutations/employee';
import { useQueryClient } from '@tanstack/react-query';
import {
	Building2,
	ChevronDown,
	ChevronUp,
	Layers,
	Link2,
	Mail,
	Plus,
	Settings,
	Trash2,
	UserRound,
	Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmployeeModal } from '../components/EmployeeModal';
import { EmployeeDeleteModal } from '../components/EmployeeDeleteModal';
import { LinkCollaboratorOrgModal } from '../components/LinkCollaboratorOrgModal';

const employeeDisplayName = (employee: Employees.Employee) => {
	if (employee.user) {
		return `${employee.user.first_name} ${
			employee.user.last_name ?? ''
		}`.trim();
	}
	return 'Usuário ainda não vinculado';
};

const resolveDeptSectorLabels = (
	employee: Employees.Employee,
	links: Companies.CompanyDepartmentLinkDetail[] | undefined,
): { dept: string; sector: string } => {
	let dept = employee.department?.name ?? null;
	let sector = employee.sector?.name ?? null;

	if (!dept && employee.department_id && links?.length) {
		const row = links.find(
			(l) => l.department?.id === employee.department_id,
		);
		dept = row?.department?.name ?? null;
		if (!sector && employee.sector_id && row?.department?.company_sector) {
			sector =
				row.department.company_sector.find(
					(s) => s.id === employee.sector_id,
				)?.name ?? null;
		}
	}

	return {
		dept: dept ?? '—',
		sector: sector ?? '—',
	};
};

type CompanyEmployeeRowProps = {
	employee: Employees.Employee;
	departmentLinks: Companies.CompanyDepartmentLinkDetail[] | undefined;
	formatCPF: (value: string) => string;
	onNavigateUser: (userId: string) => void;
	onRequestDelete: (employee: Employees.Employee) => void;
	onRequestLinkOrg: (employee: Employees.Employee) => void;
};

const CompanyEmployeeRow = ({
	employee,
	departmentLinks,
	formatCPF,
	onNavigateUser,
	onRequestDelete,
	onRequestLinkOrg,
}: CompanyEmployeeRowProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const displayName = employeeDisplayName(employee);
	const displayEmail = employee.user?.email ?? '—';
	const initials = employee.user
		? `${employee.user.first_name.charAt(0)}${
				employee.user.last_name?.charAt(0) ?? ''
		  }`
		: '?';

	const { dept: deptName, sector: sectorName } = resolveDeptSectorLabels(
		employee,
		departmentLinks,
	);

	return (
		<div className="group rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all duration-200 hover:border-stone-200">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
				<div className="flex items-center gap-4">
					<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-stone-100 text-sm font-black uppercase text-stone-600 sm:h-10 sm:w-10">
						{initials}
					</div>
					<div className="flex min-w-0 flex-col">
						<span className="line-clamp-2 text-sm font-bold leading-tight text-stone-900 sm:line-clamp-none">
							{displayName}
						</span>
						<span className="mt-1 text-[10px] font-bold uppercase tracking-tight text-stone-400 sm:mt-0 sm:text-[10px]">
							{displayEmail}
						</span>
					</div>
				</div>
				<div className="flex w-full items-center justify-between gap-3 border-t border-stone-100 pt-3 sm:w-auto sm:justify-start sm:gap-4 sm:border-t-0 sm:pt-0">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 sm:contents">
						<span className="whitespace-nowrap text-xs font-bold text-stone-600">
							{formatCPF(employee.cpf)}
						</span>
						<span
							className={`text-[9px] font-black uppercase tracking-widest ${
								employee.active
									? 'text-emerald-500'
									: 'text-stone-400'
							}`}
						>
							{employee.active ? 'Ativo' : 'Inativo'}
						</span>
					</div>
					<div className="flex items-center gap-1 sm:gap-1">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onRequestDelete(employee);
							}}
							className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-rose-50 hover:text-rose-600 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
							title="Excluir"
						>
							<span className="sm:hidden">Excluir </span>
							<Trash2 size={16} className="sm:size-4" />
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								onRequestLinkOrg(employee);
							}}
							className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-stone-900 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
							title="Vincular ao setor"
						>
							<span className="sm:hidden">Vincular </span>
							<Link2 size={16} className="sm:size-4" />
						</button>
						{employee.user_id && (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									onNavigateUser(employee.user_id as string);
								}}
								className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-stone-900 sm:rounded-lg sm:border-0 sm:px-2 sm:py-2 sm:text-stone-400"
								title="Ver usuário"
							>
								<span className="sm:hidden">Ver usuário </span>
								<Settings size={16} className="sm:size-4" />
							</button>
						)}
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setIsExpanded((prev) => !prev);
							}}
							className="flex items-center justify-center rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
							title={
								isExpanded
									? 'Recolher informações'
									: 'Ver informações'
							}
						>
							{isExpanded ? (
								<ChevronUp size={16} />
							) : (
								<ChevronDown size={16} />
							)}
						</button>
					</div>
				</div>
			</div>

			{isExpanded && (
				<div className="mt-4 grid grid-cols-1 gap-4 border-t border-stone-100 pt-4 text-sm text-stone-900 sm:grid-cols-2">
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Nome
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<UserRound className="h-4 w-4 shrink-0 text-stone-500" />
							<span>{displayName}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							E-mail
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<Mail className="h-4 w-4 shrink-0 text-stone-500" />
							<span>{displayEmail}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							CPF
						</p>
						<p className="mt-1 font-semibold text-stone-900">
							{formatCPF(employee.cpf)}
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Status
						</p>
						<p className="mt-1 font-semibold text-stone-900">
							{employee.active ? 'Ativo' : 'Inativo'}
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Departamento
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<Building2 className="h-4 w-4 shrink-0 text-stone-500" />
							<span>{deptName}</span>
						</p>
					</div>
					<div>
						<p className="text-[10px] font-black uppercase tracking-widest text-stone-400">
							Setor
						</p>
						<p className="mt-1 flex items-center gap-2 font-semibold text-stone-900">
							<Layers className="h-4 w-4 shrink-0 text-stone-500" />
							<span>{sectorName}</span>
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export const CompanyEmployees = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { data: company } = useGetCompany(id || '', {
		refetchOnWindowFocus: true,
	});
	const totalEmployees = company?.employees?.length || 0;

	const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
	const [isLinkCollaboratorModalOpen, setIsLinkCollaboratorModalOpen] =
		useState(false);
	const [linkOrgPrefillEmployeeId, setLinkOrgPrefillEmployeeId] = useState<
		string | null
	>(null);
	const [employeeToDelete, setEmployeeToDelete] =
		useState<Employees.Employee | null>(null);

	const queryClient = useQueryClient();

	const { mutateAsync: removeEmployee, isPending: isDeleting } =
		useRemoveEmployee(() => {
			setEmployeeToDelete(null);
		});
	const { mutateAsync: unlinkFromUser, isPending: isUnlinkingFromUser } =
		useUnlinkEmployeeFromUser(() =>
			queryClient.invalidateQueries({ queryKey: ['company', id] }),
		);

	const formatCPF = (value: string) => {
		const digits = value.replace(/\D/g, '');
		return digits
			.replace(/(\d{3})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d)/, '$1.$2')
			.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
	};

	return (
		<div className="flex animate-fade-in flex-col gap-6">
			<PageTitle text="Colaboradores" />
			<div className="flex items-center justify-between">
				<div className="space-y-1">
					<h3 className="text-lg font-black text-stone-900">
						Colaboradores
					</h3>
					<p className="text-sm text-stone-400">
						{totalEmployees} cadastrado
						{totalEmployees !== 1 ? 's' : ''}
					</p>
				</div>
				<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
					<button
						type="button"
						disabled={!id}
						className="inline-flex items-center justify-center gap-1.5 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-sm font-bold text-stone-900 shadow-sm transition-colors hover:bg-stone-50 disabled:pointer-events-none disabled:opacity-50"
						onClick={() => {
							setLinkOrgPrefillEmployeeId(null);
							setIsLinkCollaboratorModalOpen(true);
						}}
					>
						<Plus className="size-5 stroke-[2.5] text-stone-900" />
						Vincular ao setor
					</button>
					<Button
						text="Novo colaborador"
						className="rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800"
						startIcon={
							<Plus className="size-5 stroke-[3] text-white" />
						}
						onClick={() => setIsEmployeeModalOpen(true)}
					/>
				</div>
			</div>

			<div className="space-y-2">
				{company?.employees?.length ? (
					company?.employees.map((employee) => (
						<CompanyEmployeeRow
							key={employee.id}
							employee={employee}
							departmentLinks={company.company_department_link}
							formatCPF={formatCPF}
							onNavigateUser={(userId) =>
								navigate(`/admin/pacientes/editar/${userId}`)
							}
							onRequestDelete={setEmployeeToDelete}
							onRequestLinkOrg={(emp) => {
								setLinkOrgPrefillEmployeeId(emp.id);
								setIsLinkCollaboratorModalOpen(true);
							}}
						/>
					))
				) : (
					<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-16 text-center">
						<div className="mb-3 rounded-full bg-white p-4 text-stone-300 shadow-sm">
							<Users size={28} />
						</div>
						<p className="text-sm font-bold text-stone-600">
							Nenhum colaborador
						</p>
						<p className="mt-1 text-xs text-stone-400">
							Adicione o primeiro colaborador desta empresa.
						</p>
						<Button
							text="Novo colaborador"
							variant="outline"
							className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
							startIcon={<Plus className="size-4 stroke-[3]" />}
							onClick={() => setIsEmployeeModalOpen(true)}
						/>
					</div>
				)}
			</div>

			{id ? (
				<LinkCollaboratorOrgModal
					isOpen={isLinkCollaboratorModalOpen}
					onClose={() => {
						setIsLinkCollaboratorModalOpen(false);
						setLinkOrgPrefillEmployeeId(null);
					}}
					companyId={id}
					employees={company?.employees ?? []}
					prefilledEmployeeId={linkOrgPrefillEmployeeId}
				/>
			) : null}

			{company && (
				<>
					<EmployeeModal
						isOpen={isEmployeeModalOpen}
						onClose={() => setIsEmployeeModalOpen(false)}
						companyId={company.id}
					/>
					<EmployeeDeleteModal
						isOpen={!!employeeToDelete}
						onClose={() => setEmployeeToDelete(null)}
						onConfirm={async () => {
							if (!employeeToDelete) return;
							if (employeeToDelete.user_id) {
								await unlinkFromUser(employeeToDelete.id);
							}
							await removeEmployee(employeeToDelete.id);
						}}
						employeeName={
							employeeToDelete
								? (employeeToDelete.user
										? `${
												employeeToDelete.user.first_name
										  } ${
												employeeToDelete.user
													.last_name ?? ''
										  }`.trim()
										: `CPF ${formatCPF(
												employeeToDelete.cpf,
										  )}`) || 'Colaborador'
								: ''
						}
						hasLinkedUser={!!employeeToDelete?.user_id}
						isDeleting={isDeleting || isUnlinkingFromUser}
					/>
				</>
			)}
		</div>
	);
};
